import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  ClipboardCheck,
  DollarSign,
  FileText,
  ImageOff,
  type LucideIcon,
  PackageCheck,
  Receipt,
  RotateCcw,
  ShoppingCart,
  Tag,
  Truck,
  Undo2,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import {
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ChipGroup } from '@/ui/Filters';
import { DataTable, type Column } from '@/ui/DataTable';
import { useShortcutHint } from '@/app/StatusLine';
import { money, moneyCompact, num, time } from '@/lib/format';
import { usePurchasingToday } from '@/features/buying/today/api';
import {
  purchasingTodayItems,
  type TodayItemId,
  type TodayLink,
} from '@/features/buying/today/model';
import { TodayLinkTo } from '@/features/buying/today/TodayLinkTo';
import type { PurchasingToday } from '@/features/buying/today/types';

/**
 * Today — the operator's home.
 *
 * Two questions, answered above the fold: *how is the day going* (the KPI row
 * and the rhythm strip) and *what needs me right now* (the action queue). Every
 * queue entry is a real count from a real endpoint and links to the screen that
 * clears it; an empty queue renders nothing at all, because a grey zero reads
 * as an alarm that has already been dismissed.
 *
 * Honesty rules applied here:
 *  · A delta is only drawn when the comparison window is genuinely comparable —
 *    today-so-far against yesterday up to the same clock minute. Never a
 *    part-day against a whole day.
 *  · A queue leg that fails (403 for a cashier, endpoint down) resolves to
 *    `null`, not 0, and its row is omitted rather than claiming "all clear".
 *  · The hourly strip is built from the transaction list, which is capped at
 *    500 rows; when the day exceeds that the panel says so instead of drawing
 *    a silently truncated shape.
 *  · TASK-0250 (FR-TODAY): the purchasing rows (deliveries that look missing,
 *    invoices waiting, photos that could not be read, held-back lines, shelf
 *    labels) and the two purchase-order rows come from one read,
 *    `GET /purchasing/today`, and count only real work: open orders, imports
 *    of the last 14 days. Each opens the list its count was taken from.
 */

/* ── Wire shapes ──────────────────────────────────────────────────────────────
   Declared locally and narrowly. `lib/api/types.ts` is hand-maintained and, for
   the two endpoints below, currently advertises fields the routes do not send
   (see the notes on TxnRow), so these mirror the verified route selects.     */

type SalesReportRow = {
  label: string;
  revenue: number;
  count: number;
  tax?: number;
};

/** `GET /transactions` row — only the columns the route actually selects. */
type TxnRow = {
  id: string;
  occurredAt: string;
  status: string;
  total: number;
  taxTotal: number | null;
  itemCount: number | null;
  cashierName: string | null;
  customerName: string | null;
  paymentMethod: string;
};

type TxnListResponse = {
  rows: TxnRow[];
  total: number;
  limit?: number;
  offset?: number;
  summary?: {
    count: number;
    gross: number;
    completed: number;
    refunds: number;
    voided: number;
    suspended?: number;
    itemCount?: number;
  };
};

type Anomaly = {
  type: string;
  severity: 'high' | 'medium' | string;
  message: string;
  details?: Record<string, unknown>;
};

/* ── Time windows ─────────────────────────────────────────────────────────── */

/** Ticks every minute, rounded down to 5, so query keys are stable but the
 *  "same time yesterday" comparison never drifts more than five minutes. */
function useAnchorMinute(): number {
  const round = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    d.setMinutes(Math.floor(d.getMinutes() / 5) * 5);
    return d.getTime();
  };
  const [anchor, setAnchor] = useState(round);
  useEffect(() => {
    const id = window.setInterval(() => setAnchor(round()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return anchor;
}

const DAY_MS = 86_400_000;

function startOfDay(t: number): Date {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Percent change, or null when the base period has nothing to compare to. */
function delta(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function sumRevenue(rows: SalesReportRow[] | undefined): number {
  return (rows ?? []).reduce((acc, r) => acc + r.revenue, 0);
}
function sumCount(rows: SalesReportRow[] | undefined): number {
  return (rows ?? []).reduce((acc, r) => acc + r.count, 0);
}
function sumTax(rows: SalesReportRow[] | undefined): number {
  return (rows ?? []).reduce((acc, r) => acc + (r.tax ?? 0), 0);
}

/* ── Action queue ─────────────────────────────────────────────────────────────
   One query with independently-settled legs, mirroring `app/useBadges`. A leg
   that throws yields null — "we do not know" — which is materially different
   from zero and must not be drawn as an all-clear.                          */

type QueueCounts = {
  ordersSubmitted: number | null;
  ordersPicking: number | null;
  lowStock: number | null;
  lowStockCapped: boolean;
  returnsPending: number | null;
  costChanges: number | null;
  quarantine: number | null;
};

async function settle<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch {
    return null;
  }
}

const LOW_STOCK_LIMIT = 50; // server-side cap on GET /inventory/low-stock

function useActionQueue() {
  return useQuery({
    queryKey: ['today', 'queue'],
    refetchInterval: 60_000,
    staleTime: 45_000,
    queryFn: async (): Promise<QueueCounts> => {
      // TASK-0250: the purchase-order rows come from GET /purchasing/today (open orders only).
      const [
        ordersSubmitted,
        ordersPicking,
        lowStockRows,
        returnsPending,
        costChanges,
        quarantineRows,
      ] = await Promise.all([
        settle(
          async () =>
            (
              await api.get<{ total: number }>('/orders', {
                params: { status: 'submitted', pageSize: 1 },
              })
            ).total ?? 0
        ),
        settle(
          async () =>
            (
              await api.get<{ total: number }>('/orders', {
                params: { status: 'picking', pageSize: 1 },
              })
            ).total ?? 0
        ),
        settle(() => api.get<unknown[]>('/inventory/low-stock')),
        settle(
          async () =>
            (
              await api.get<{ total: number }>('/return-reviews', {
                params: { status: 'pending', pageSize: 1 },
              })
            ).total ?? 0
        ),
        settle(
          async () =>
            (await api.get<{ count: number }>('/vendors/cost-changes/count')).count ?? 0
        ),
        settle(() => api.get<unknown[]>('/admin/sync-quarantine')),
      ]);

      const lowStock = Array.isArray(lowStockRows) ? lowStockRows.length : null;
      return {
        ordersSubmitted,
        ordersPicking,
        lowStock,
        lowStockCapped: lowStock === LOW_STOCK_LIMIT,
        returnsPending,
        costChanges,
        quarantine: Array.isArray(quarantineRows) ? quarantineRows.length : null,
      };
    },
  });
}

/* ── Screen ───────────────────────────────────────────────────────────────── */

type Rhythm = 'hourly' | 'daily';

export const Route = createFileRoute('/_app/')({
  staticData: { crumb: 'Today' },
  validateSearch: (s: Record<string, unknown>) => ({
    rhythm: (s.rhythm === 'daily' ? 'daily' : 'hourly') as Rhythm,
  }),
  component: TodayScreen,
});

function TodayScreen() {
  const { rhythm } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const anchor = useAnchorMinute();

  useShortcutHint([{ keys: 'R', label: 'Rhythm' }]);

  const setRhythm = (next: Rhythm) => {
    void navigate({ search: { rhythm: next }, replace: true });
  };

  // `R` flips the rhythm strip. Ignored while typing anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        (t &&
          (t.tagName === 'INPUT' ||
            t.tagName === 'TEXTAREA' ||
            t.tagName === 'SELECT' ||
            t.isContentEditable))
      ) {
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        void navigate({
          search: (prev: { rhythm: Rhythm }) => ({
            rhythm: prev.rhythm === 'hourly' ? ('daily' as const) : ('hourly' as const),
          }),
          replace: true,
        });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const windows = useMemo(() => {
    const todayStart = startOfDay(anchor);
    const elapsedMs = anchor - todayStart.getTime();
    const yesterdayStart = new Date(todayStart.getTime() - DAY_MS);
    return {
      todayStart: todayStart.toISOString(),
      yesterdayStart: yesterdayStart.toISOString(),
      yesterdayCut: new Date(yesterdayStart.getTime() + elapsedMs).toISOString(),
      trendStart: startOfDay(anchor - 13 * DAY_MS).toISOString(),
      dayLabel: todayStart,
    };
  }, [anchor]);

  const today = useQuery({
    queryKey: ['today', 'sales', windows.todayStart],
    queryFn: () =>
      api.get<SalesReportRow[]>('/reports/sales', {
        params: { date_from: windows.todayStart },
      }),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });

  const compare = useQuery({
    queryKey: ['today', 'sales-compare', windows.yesterdayStart, windows.yesterdayCut],
    queryFn: () =>
      api.get<SalesReportRow[]>('/reports/sales', {
        params: { date_from: windows.yesterdayStart, date_to: windows.yesterdayCut },
      }),
    staleTime: 5 * 60_000,
  });

  const trend = useQuery({
    queryKey: ['today', 'sales-trend', windows.trendStart],
    queryFn: () =>
      api.get<SalesReportRow[]>('/reports/sales', {
        params: { date_from: windows.trendStart },
      }),
    staleTime: 10 * 60_000,
  });

  const txns = useQuery({
    queryKey: ['today', 'transactions', windows.todayStart],
    queryFn: () =>
      api.get<TxnListResponse>('/transactions', {
        params: { date_from: windows.todayStart, limit: 500 },
      }),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });

  const itemsSold = useQuery({
    queryKey: ['today', 'items', windows.todayStart],
    queryFn: () =>
      api.get<TxnListResponse>('/transactions', {
        params: { date_from: windows.todayStart, status: 'completed', limit: 1 },
      }),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });

  const queue = useActionQueue();
  const purchasing = usePurchasingToday();

  const anomalies = useQuery({
    queryKey: ['today', 'anomalies'],
    // A failing anomaly feed means "nothing to report", never a red screen —
    // it is advisory intelligence, not an operational fact.
    queryFn: () => api.get<Anomaly[]>('/ai/anomalies').catch(() => [] as Anomaly[]),
    staleTime: 5 * 60_000,
    retry: false,
  });

  /**
   * Liveness is probed with `/api/version`, not the API's `/health` — the same
   * reasoning as `app/StatusLine.tsx`, and the same query key so the two agree.
   *
   * `/health` is mounted at the *server root*, and nothing routes the root to
   * the API: the vite dev proxy only forwards `/api`, and in production Caddy
   * sends `/api/*` to the API container and everything else to the front-end.
   * A `/health` probe from the browser therefore always failed, which lit the
   * red banner below on a perfectly healthy store. `/api/version` is
   * unauthenticated, cheap and reachable in every environment.
   */
  const health = useQuery({
    queryKey: ['version'],
    queryFn: () => api.get<{ version: string; buildSha: string | null }>('/version'),
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: 1,
  });

  const revenue = sumRevenue(today.data);
  const txCount = sumCount(today.data);
  const tax = sumTax(today.data);
  const basket = txCount > 0 ? Math.round(revenue / txCount) : 0;

  const prevRevenue = sumRevenue(compare.data);
  const prevCount = sumCount(compare.data);
  const prevTax = sumTax(compare.data);
  const prevBasket = prevCount > 0 ? Math.round(prevRevenue / prevCount) : 0;
  const comparable = compare.isSuccess;

  const items = itemsSold.data?.summary?.itemCount ?? null;

  const loadingKpis = today.isPending;

  // The caption only earns its place next to a figure it explains: yesterday at
  // zero makes the change undefined, and "vs yesterday, same time" with nothing
  // beside it reads as a number that failed to render.
  const dRevenue = comparable ? delta(revenue, prevRevenue) : null;
  const dCount = comparable ? delta(txCount, prevCount) : null;
  const dBasket = comparable ? delta(basket, prevBasket) : null;
  const dTax = comparable ? delta(tax, prevTax) : null;
  const vsYesterday = 'vs yesterday, same time';

  return (
    <Page>
      <PageHeader
        eyebrow="Overview"
        title="Today"
        subtitle={windows.dayLabel.toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        })}
        actions={
          <Button
            size="sm"
            onClick={() => {
              void today.refetch();
              void txns.refetch();
              void itemsSold.refetch();
              void queue.refetch();
              void purchasing.refetch();
            }}
            loading={
              today.isFetching ||
              txns.isFetching ||
              queue.isFetching ||
              purchasing.isFetching
            }
          >
            Refresh
          </Button>
        }
      />

      <PageBody className="flex flex-col gap-3">
        <HealthBanner unhealthy={health.isError} anomalies={anomalies.data ?? []} />

        {today.isError ? (
          <ErrorState
            title="Today's sales could not be loaded"
            message={String((today.error as Error | undefined)?.message ?? '')}
            onRetry={() => void today.refetch()}
          />
        ) : (
          <StatRow className="xl:grid-cols-5">
            <Stat
              label="Sales incl. tax"
              value={loadingKpis ? <Skeleton className="h-6 w-24" /> : money(revenue)}
              delta={dRevenue}
              sub={dRevenue === null ? undefined : vsYesterday}
            />
            <Stat
              label="Transactions"
              value={loadingKpis ? <Skeleton className="h-6 w-16" /> : num(txCount)}
              delta={dCount}
              sub={dCount === null ? undefined : vsYesterday}
            />
            <Stat
              label="Average basket"
              value={loadingKpis ? <Skeleton className="h-6 w-20" /> : money(basket)}
              delta={dBasket}
              sub={dBasket === null ? undefined : vsYesterday}
            />
            <Stat
              label="Tax collected"
              value={loadingKpis ? <Skeleton className="h-6 w-20" /> : money(tax)}
              delta={dTax}
              goodDirection="none"
              sub={dTax === null ? undefined : vsYesterday}
            />
            <Stat
              label="Items sold"
              value={
                itemsSold.isPending ? (
                  <Skeleton className="h-6 w-16" />
                ) : items === null ? (
                  '—'
                ) : (
                  num(items)
                )
              }
              sub="completed sales"
            />
          </StatRow>
        )}

        <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-3 xl:col-span-2">
            <RhythmPanel
              rhythm={rhythm}
              onRhythmChange={setRhythm}
              txns={txns.data}
              txnsPending={txns.isPending}
              trend={trend.data}
              trendPending={trend.isPending}
            />
            <RecentActivity
              rows={txns.data?.rows}
              pending={txns.isPending}
              error={txns.error}
              onRetry={() => void txns.refetch()}
            />
          </div>

          <ActionQueue
            counts={queue.data}
            purchasing={purchasing.isError ? null : purchasing.data}
            purchasingFailed={purchasing.isError}
            onRetryPurchasing={() => void purchasing.refetch()}
            pending={queue.isPending || purchasing.isPending}
          />
        </div>
      </PageBody>
    </Page>
  );
}

/* ── Health / anomaly banner ──────────────────────────────────────────────── */

function HealthBanner({
  unhealthy,
  anomalies,
}: {
  unhealthy: boolean;
  anomalies: Anomaly[];
}) {
  const high = anomalies.filter((a) => a.severity === 'high');
  const rest = anomalies.filter((a) => a.severity !== 'high');
  if (!unhealthy && anomalies.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {unhealthy ? (
        <div className="border-negative/40 bg-negative-muted flex items-start gap-2 rounded border px-3 py-2">
          <AlertTriangle className="text-negative mt-0.5 size-3.5 shrink-0" />
          <div className="text-ink min-w-0 text-sm">
            <span className="text-negative font-medium">The API is not responding.</span>{' '}
            Figures on this screen may be stale. Registers keep selling offline; check
            sync health before acting on anything here.
          </div>
        </div>
      ) : null}

      {high.map((a, i) => (
        <div
          key={`${a.type}-${i}`}
          className="border-negative/40 bg-negative-muted flex items-start gap-2 rounded border px-3 py-2"
        >
          <AlertTriangle className="text-negative mt-0.5 size-3.5 shrink-0" />
          <div className="text-ink min-w-0 flex-1 text-sm">{a.message}</div>
          <Badge tone="negative">{a.type}</Badge>
        </div>
      ))}

      {rest.length > 0 ? (
        <div className="border-line bg-surface rounded border">
          <div className="border-line text-2xs text-ink-muted border-b px-3 py-1.5 font-semibold tracking-wider uppercase">
            Watch list
          </div>
          <ul className="divide-line-soft divide-y">
            {rest.map((a, i) => (
              <li
                key={`${a.type}-${i}`}
                className="text-ink-muted flex items-start gap-2 px-3 py-1.5 text-xs"
              >
                <span
                  className="bg-warn mt-1.5 size-1.5 shrink-0 rounded-full"
                  aria-hidden
                />
                <span className="min-w-0 flex-1">{a.message}</span>
                <Badge tone="warn">{a.type}</Badge>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/* ── Sales rhythm ─────────────────────────────────────────────────────────────
   A hand-drawn SVG bar strip. No chart library: the shape is eight rectangles
   and a baseline, and a library would cost 40kB to draw it slightly worse.
   Colour comes from tokens only (`fill-signal` / `fill-line-strong`), so it
   flips with the theme like everything else.                                */

type Bar = {
  key: string;
  label: string;
  value: number;
  count: number;
  current?: boolean;
};

function Sparkbars({ bars }: { bars: Bar[] }) {
  const max = bars.reduce((m, b) => Math.max(m, b.value), 0);
  const W = Math.max(bars.length, 1) * 10;
  const H = 60;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="h-24 w-full"
      role="img"
      aria-label="Sales including tax by period"
    >
      {bars.map((b, i) => {
        const h = max > 0 ? Math.max(b.value > 0 ? 1 : 0, (b.value / max) * (H - 2)) : 0;
        return (
          <rect
            key={b.key}
            x={i * 10 + 1}
            y={H - h}
            width={8}
            height={h}
            className={cn(b.current ? 'fill-signal' : 'fill-line-strong')}
          >
            <title>{`${b.label} · ${money(b.value)} · ${num(b.count)} txns`}</title>
          </rect>
        );
      })}
      <rect x={0} y={H - 0.5} width={W} height={0.5} className="fill-line" />
    </svg>
  );
}

function RhythmPanel({
  rhythm,
  onRhythmChange,
  txns,
  txnsPending,
  trend,
  trendPending,
}: {
  rhythm: Rhythm;
  onRhythmChange: (r: Rhythm) => void;
  txns: TxnListResponse | undefined;
  txnsPending: boolean;
  trend: SalesReportRow[] | undefined;
  trendPending: boolean;
}) {
  const truncated = !!txns && txns.total > txns.rows.length;

  const hourly = useMemo<Bar[]>(() => {
    if (!txns) return [];
    const buckets = new Map<number, { value: number; count: number }>();
    for (const row of txns.rows) {
      // Only money-bearing statuses, matching the reports convention.
      if (row.status !== 'completed' && row.status !== 'refund') continue;
      const hour = new Date(row.occurredAt).getHours();
      const b = buckets.get(hour) ?? { value: 0, count: 0 };
      b.value += row.total;
      b.count += 1;
      buckets.set(hour, b);
    }
    // No takings yet is an empty series, not a one-bar series worth $0 — the
    // latter drew a flat axis reading "10a 10a 10a" and a $0.00 "peak hour".
    if (buckets.size === 0) return [];
    const nowHour = new Date().getHours();
    const hours = [...buckets.keys()];
    const first = hours.length > 0 ? Math.min(...hours, nowHour) : nowHour;
    const out: Bar[] = [];
    for (let h = first; h <= nowHour; h++) {
      const b = buckets.get(h);
      out.push({
        key: String(h),
        label: hourLabel(h),
        value: b?.value ?? 0,
        count: b?.count ?? 0,
        current: h === nowHour,
      });
    }
    return out;
  }, [txns]);

  const daily = useMemo<Bar[]>(() => {
    if (!trend) return [];
    return trend.map((r, i) => ({
      key: r.label,
      label: r.label,
      value: r.revenue,
      count: r.count,
      current: i === trend.length - 1,
    }));
  }, [trend]);

  const bars = rhythm === 'hourly' ? hourly : daily;
  const pending = rhythm === 'hourly' ? txnsPending : trendPending;
  const hasSales = bars.some((b) => b.value !== 0);
  const peak = !hasSales
    ? null
    : bars.reduce<Bar | null>(
        (best, b) => (best === null || b.value > best.value ? b : best),
        null
      );

  /** First / middle / last, de-duplicated — a two-bar day must not print the
   *  same label three times across the axis. */
  const ticks = useMemo(() => {
    if (bars.length === 0) return [];
    const picks = [0, Math.floor((bars.length - 1) / 2), bars.length - 1];
    return [...new Set(picks)].map((i) => bars[i]!.label);
  }, [bars]);

  return (
    <Panel
      title="Sales rhythm"
      description={
        rhythm === 'hourly'
          ? 'Sales incl. tax by hour, today'
          : 'Sales incl. tax by day, last 14 days'
      }
      actions={
        <ChipGroup<Rhythm>
          value={rhythm}
          onChange={onRhythmChange}
          options={[
            { value: 'hourly', label: 'Hourly' },
            { value: 'daily', label: '14 days' },
          ]}
        />
      }
      footer={
        rhythm === 'hourly' && truncated ? (
          <span className="text-warn">
            {num(txns?.total ?? 0)} transactions today exceeds the 500-row list cap — this
            strip covers only the most recent 500. Use the 14-day view for a complete
            shape.
          </span>
        ) : peak ? (
          <span>
            Peak {rhythm === 'hourly' ? 'hour' : 'day'}{' '}
            <span className="text-ink font-mono">{peak.label}</span> at{' '}
            <span className="text-ink font-mono">{money(peak.value)}</span>
          </span>
        ) : null
      }
    >
      {pending ? (
        <Skeleton className="h-24 w-full" />
      ) : !hasSales ? (
        <div className="dot-grid flex h-24 flex-col items-center justify-center gap-1 rounded text-center">
          <div className="text-ink-muted text-sm">
            {rhythm === 'hourly'
              ? 'No sales recorded yet today'
              : 'No sales in the last 14 days'}
          </div>
          <div className="text-2xs text-ink-faint">
            {rhythm === 'hourly'
              ? 'The strip fills in as registers sync.'
              : 'Nothing to chart for this window.'}
          </div>
        </div>
      ) : (
        <>
          <Sparkbars bars={bars} />
          <div
            className={cn(
              'text-2xs text-ink-faint mt-1 flex font-mono',
              ticks.length > 1 ? 'justify-between' : 'justify-center'
            )}
          >
            {ticks.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
          <div className="border-line-soft text-2xs text-ink-faint mt-2 flex items-baseline gap-4 border-t pt-2">
            <span>
              Max{' '}
              <span className="text-ink-muted font-mono">
                {moneyCompact(bars.reduce((m, b) => Math.max(m, b.value), 0))}
              </span>
            </span>
            <span>
              Total{' '}
              <span className="text-ink-muted font-mono">
                {money(bars.reduce((s, b) => s + b.value, 0))}
              </span>
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}

function hourLabel(h: number): string {
  const suffix = h < 12 ? 'a' : 'p';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}${suffix}`;
}

/* ── Recent activity ──────────────────────────────────────────────────────── */

const STATUS_TONE: Record<string, Tone> = {
  completed: 'positive',
  refund: 'warn',
  voided: 'negative',
  suspended: 'info',
  training: 'neutral',
};

function RecentActivity({
  rows,
  pending,
  error,
  onRetry,
}: {
  rows: TxnRow[] | undefined;
  pending: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const recent = (rows ?? []).slice(0, 8);
  const transactionsPath: string = '/transactions';

  const columns: Array<Column<TxnRow>> = [
    {
      id: 'time',
      header: 'Time',
      width: '5rem',
      mono: true,
      cell: (r) => <span className="text-xs">{time(r.occurredAt)}</span>,
    },
    {
      id: 'cashier',
      header: 'Cashier',
      cell: (r) => (
        <span className="text-ink-muted truncate text-xs">{r.cashierName ?? '—'}</span>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      hideBelow: 'lg',
      cell: (r) => (
        <span className="text-ink-faint truncate text-xs">{r.customerName ?? '—'}</span>
      ),
    },
    {
      id: 'items',
      header: 'Items',
      width: '4rem',
      align: 'right',
      mono: true,
      cell: (r) => <span className="text-xs">{r.itemCount ?? '—'}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '6rem',
      cell: (r) => <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>{r.status}</Badge>,
    },
    {
      id: 'total',
      header: 'Total',
      width: '6rem',
      align: 'right',
      mono: true,
      cell: (r) => <span className="text-xs">{money(r.total)}</span>,
    },
  ];

  return (
    <Panel
      title="Recent activity"
      bleed
      actions={
        <Button variant="ghost" size="xs" asChild>
          <Link to={transactionsPath}>
            All transactions
            <ArrowRight className="size-3" />
          </Link>
        </Button>
      }
    >
      <DataTable
        rows={pending ? undefined : recent}
        columns={columns}
        rowKey={(r) => r.id}
        loading={pending}
        error={error}
        onRetry={onRetry}
        keyboard={false}
        empty={{
          icon: <Receipt />,
          title: 'No sales yet today',
          description: 'Transactions appear here within a minute of a register sync.',
        }}
      />
    </Panel>
  );
}

/* ── Action queue ─────────────────────────────────────────────────────────── */

type QueueItem = {
  id: string;
  icon: LucideIcon;
  label: string;
  detail: string;
  count: number;
  tone: Tone;
} & ({ to: string; link?: undefined } | { to?: undefined; link: TodayLink });

/** TASK-0250: an icon for each purchasing-today row. */
const PURCHASING_ICONS: Record<TodayItemId, LucideIcon> = {
  deliveries: Truck,
  invoices: FileText,
  unreadable: ImageOff,
  'held-back': Undo2,
  labels: Tag,
};

/**
 * `c` is the independently-settled legs; `p` the purchasing read (null when it
 * failed, so its rows are left out rather than shown as clear).
 */
function buildQueue(
  c: QueueCounts | undefined,
  p: PurchasingToday | null | undefined
): QueueItem[] {
  if (!c) return [];
  const items: QueueItem[] = [];
  const purchasing = purchasingTodayItems(p);
  const purchasingRow = (id: TodayItemId) => {
    const item = purchasing.find((i) => i.id === id);
    if (!item) return;
    items.push({
      id: `purchasing-${item.id}`,
      icon: PURCHASING_ICONS[item.id],
      label: item.label,
      detail: item.detail,
      count: item.count,
      tone: item.tone,
      link: item.link,
    });
  };

  if (c.quarantine) {
    items.push({
      id: 'quarantine',
      icon: AlertTriangle,
      label: 'Sync quarantine',
      detail: 'Financial records the API could not apply',
      count: c.quarantine,
      tone: 'negative',
      to: '/system/sync',
    });
  }
  if (c.ordersSubmitted) {
    items.push({
      id: 'orders-submitted',
      icon: ShoppingCart,
      label: 'Orders waiting to be picked',
      detail: 'Submitted, nobody has claimed them',
      count: c.ordersSubmitted,
      tone: 'signal',
      to: '/orders',
    });
  }
  if (c.ordersPicking) {
    items.push({
      id: 'orders-picking',
      icon: PackageCheck,
      label: 'Orders being picked',
      detail: 'In progress on the floor',
      count: c.ordersPicking,
      tone: 'info',
      to: '/orders',
    });
  }
  if (c.returnsPending) {
    items.push({
      id: 'returns',
      icon: RotateCcw,
      label: 'Returns awaiting review',
      detail: 'Approving one writes a restock adjustment',
      count: c.returnsPending,
      tone: 'warn',
      to: '/returns',
    });
  }
  purchasingRow('deliveries');
  purchasingRow('invoices');
  purchasingRow('unreadable');
  purchasingRow('held-back');
  pushOpenOrders(items, p);
  if (c.costChanges) {
    items.push({
      id: 'cost-changes',
      icon: DollarSign,
      label: 'Vendor cost changes to approve',
      detail: 'Cost moved on receipt — approve or reject',
      count: c.costChanges,
      tone: 'warn',
      to: '/vendors/cost-approvals',
    });
  }
  if (c.lowStock) {
    items.push({
      id: 'low-stock',
      icon: Boxes,
      label: c.lowStockCapped
        ? 'Products at or below reorder point (50+)'
        : 'Products at or below reorder point',
      detail: 'Tracked products only',
      count: c.lowStock,
      tone: 'warn',
      to: '/inventory',
    });
  }
  purchasingRow('labels');
  return items;
}

/**
 * The two purchase-order rows. Since TASK-0250 they count open orders only
 * (sent, not all in, not closed short, not made from an invoice, not history),
 * so the reversed invoice POs and old drafts never show here.
 */
function pushOpenOrders(items: QueueItem[], p: PurchasingToday | null | undefined) {
  if (!p) return;
  if (p.openOrders.partlyReceived) {
    items.push({
      id: 'po-partial',
      icon: Truck,
      label: 'Purchase orders part-received',
      detail: 'Open lines still to receive',
      count: p.openOrders.partlyReceived,
      tone: 'warn',
      to: '/purchasing',
    });
  }
  if (p.openOrders.notReceived) {
    items.push({
      id: 'po-submitted',
      icon: Truck,
      label: 'Purchase orders to receive',
      detail: 'Marked as ordered, nothing booked in yet',
      count: p.openOrders.notReceived,
      tone: 'info',
      to: '/purchasing',
    });
  }
}

/**
 * "Needs attention". When the purchasing read failed (`purchasingFailed`), its
 * rows and the two purchase-order rows are missing, so the panel says so in one
 * muted line and never shows the all-clear: open orders and invoices may be
 * waiting that nobody could count (TASK-0250 review m1).
 *
 * Exported for its test only.
 */
export function ActionQueue({
  counts,
  purchasing,
  purchasingFailed = false,
  onRetryPurchasing,
  pending,
}: {
  counts: QueueCounts | undefined;
  purchasing: PurchasingToday | null | undefined;
  purchasingFailed?: boolean;
  onRetryPurchasing?: () => void;
  pending: boolean;
}) {
  const items = buildQueue(counts, purchasing);
  const total = items.reduce((s, i) => s + i.count, 0);
  const nothingCounted = items.length === 0 && purchasingFailed;

  return (
    <Panel
      title="Needs attention"
      description={
        pending || nothingCounted
          ? undefined
          : `${num(total)} across ${items.length} queues`
      }
      bleed
      className="min-w-0"
    >
      {!pending && purchasingFailed ? (
        <div
          role="status"
          className="border-line-soft text-2xs text-ink-faint flex items-center gap-2 border-b px-3 py-2"
        >
          <span className="min-w-0 flex-1">
            Purchasing counts did not load. Orders, invoices, deliveries and shelf labels
            are not counted here.
          </span>
          {onRetryPurchasing ? (
            <button
              type="button"
              onClick={onRetryPurchasing}
              className="text-ink-muted hover:text-ink shrink-0 underline-offset-2 hover:underline"
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : null}
      {pending ? (
        <div className="flex flex-col gap-2 p-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : nothingCounted ? null : items.length === 0 ? (
        <div className="dot-grid flex flex-col items-center justify-center gap-2 p-8 text-center">
          <div className="border-line bg-surface text-positive flex size-9 items-center justify-center rounded border">
            <ClipboardCheck className="size-4" />
          </div>
          <div className="text-ink text-sm font-medium">Every queue is clear</div>
          <div className="text-ink-faint max-w-xs text-xs leading-relaxed">
            No orders waiting, nothing to receive, no reviews pending. Queues that could
            not be read are not counted here.
          </div>
        </div>
      ) : (
        <ul className="divide-line-soft divide-y">
          {items.map((item) => {
            const rowClass =
              'group hover:bg-surface-hover relative flex items-center gap-3 px-3 py-2';
            const body = <QueueRowBody item={item} />;
            return (
              <li key={item.id}>
                {item.link ? (
                  <TodayLinkTo link={item.link} className={rowClass}>
                    {body}
                  </TodayLinkTo>
                ) : (
                  <Link to={item.to} className={rowClass}>
                    {body}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function QueueRowBody({ item }: { item: QueueItem }) {
  return (
    <>
      <span
        className={cn(
          'absolute inset-y-0 left-0 w-0.5',
          item.tone === 'negative' && 'bg-negative',
          item.tone === 'warn' && 'bg-warn',
          item.tone === 'signal' && 'bg-signal',
          item.tone === 'info' && 'bg-info',
          item.tone === 'positive' && 'bg-positive',
          item.tone === 'neutral' && 'bg-line'
        )}
        aria-hidden
      />
      <item.icon
        className={cn(
          'size-3.5 shrink-0',
          item.tone === 'negative' ? 'text-negative' : 'text-ink-faint'
        )}
        strokeWidth={1.75}
      />
      <span className="min-w-0 flex-1">
        <span className="text-ink block truncate text-sm">{item.label}</span>
        <span className="text-2xs text-ink-faint block truncate" title={item.detail}>
          {item.detail}
        </span>
      </span>
      <span
        className={cn(
          'shrink-0 font-mono text-base tabular-nums',
          item.tone === 'negative' ? 'text-negative' : 'text-ink'
        )}
      >
        {num(item.count)}
      </span>
      <ArrowRight className="text-ink-faint size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
    </>
  );
}
